/**
 * Storage Health Page
 *
 * Read-only diagnostic of the document storage provider (Cloudflare R2 / local)
 * plus a "Broken files" checklist: every document whose underlying file is
 * missing from storage, each with an inline one-click "Re-upload" button that
 * restores the file in place (POST /api/documents/{id}/replace-file).
 *
 * RBAC (backend): Super Admin + HR Manager only.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  HardDrive, CheckCircle, XCircle, AlertTriangle, RefreshCw,
  Upload, Loader2, Cloud, ShieldAlert, FileWarning, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_ACCEPT = '.pdf,.jpg,.jpeg,.png,.doc,.docx';

const StatCard = ({ title, value, icon: Icon, colorClass, testid }) => (
  <Card data-testid={testid} className="h-full">
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <div className={`p-2 rounded-lg ${colorClass ? colorClass.replace('text-', 'bg-').replace('-600', '-100') : 'bg-slate-100'}`}>
          <Icon className={`h-4 w-4 ${colorClass || 'text-slate-600'}`} />
        </div>
      </div>
      <h3 className={`text-3xl font-bold ${colorClass || 'text-slate-900'}`}>{value}</h3>
    </CardContent>
  </Card>
);

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
};

const StorageHealthPage = () => {
  const { token } = useAuth();
  const { isRTL } = useLanguage();
  const t = (en, ar) => (isRTL ? ar : en);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reuploadingId, setReuploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const targetIdRef = useRef(null);

  const fetchHealth = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/documents/storage-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error || `Request failed (${res.status})`);
      }
      setData(await res.json());
    } catch (e) {
      setError(e.message || 'Failed to load storage health');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const triggerPick = (docId) => {
    targetIdRef.current = docId;
    fileInputRef.current?.click();
  };

  const downloadCsv = () => {
    const rows = data?.broken_documents || [];
    if (rows.length === 0) return;
    const headers = ['Document Name', 'File Name', 'Scope', 'Owner', 'Employee Number',
      'Uploaded At', 'Reason', 'Document ID', 'Storage Provider', 'Storage Key'];
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(',')];
    rows.forEach((d) => {
      lines.push([
        d.name, d.file_name, d.scope, d.owner, d.employee_number,
        d.uploaded_at, d.reason, d.id, d.storage_provider, d.storage_key,
      ].map(esc).join(','));
    });
    // BOM so Excel opens UTF-8 (Arabic names) correctly.
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `broken-documents-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleFileChosen = async (event) => {
    const file = event.target.files?.[0];
    const docId = targetIdRef.current;
    if (event.target) event.target.value = '';
    if (!file || !docId) return;

    setReuploadingId(docId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_URL}/api/documents/${docId}/replace-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail?.error || `Upload failed (${res.status})`);
      }
      toast.success(t('File re-uploaded — document restored', 'تم رفع الملف — تمت استعادة المستند'));
      // Optimistically drop the fixed row, then refresh counts.
      setData((prev) => prev ? {
        ...prev,
        ok: (prev.ok || 0) + 1,
        broken: Math.max(0, (prev.broken || 0) - 1),
        broken_documents: (prev.broken_documents || []).filter((d) => d.id !== docId),
      } : prev);
    } catch (e) {
      toast.error(e.message || t('Re-upload failed', 'فشل إعادة الرفع'));
    } finally {
      setReuploadingId(null);
      targetIdRef.current = null;
    }
  };

  const provider = data?.provider_in_use;
  const healthy = data?.provider_healthy;
  const conn = data?.storage_diagnostics?.connection;
  const broken = data?.broken_documents || [];

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="storage-health-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HardDrive className="h-6 w-6 text-slate-600" />
            {t('Storage Health', 'صحة التخزين')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {t('Document storage provider status and broken-file backlog.',
               'حالة مزود تخزين المستندات وقائمة الملفات المفقودة.')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadCsv}
            disabled={loading || !((data?.broken_documents || []).length)}
            data-testid="storage-health-download-csv-btn"
          >
            <Download className="h-4 w-4 me-2" />
            {t('Download broken-files list (CSV)', 'تنزيل قائمة الملفات المفقودة (CSV)')}
          </Button>
          <Button variant="outline" onClick={fetchHealth} disabled={loading} data-testid="storage-health-refresh-btn">
            <RefreshCw className={`h-4 w-4 me-2 ${loading ? 'animate-spin' : ''}`} />
            {t('Refresh', 'تحديث')}
          </Button>
        </div>
      </div>

      {/* Hidden file input reused by every row */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_ACCEPT}
        className="hidden"
        onChange={handleFileChosen}
        data-testid="storage-health-reupload-input"
      />

      {error && (
        <Card className="border-red-200">
          <CardContent className="p-4 flex items-center gap-3 text-red-700">
            <ShieldAlert className="h-5 w-5" />
            <span data-testid="storage-health-error">{error}</span>
          </CardContent>
        </Card>
      )}

      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : data ? (
        <>
          {/* Provider status */}
          <Card data-testid="storage-provider-card" className={healthy ? 'border-green-200' : 'border-red-200'}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Cloud className="h-5 w-5 text-slate-600" />
                {t('Storage Provider', 'مزود التخزين')}
              </CardTitle>
              <CardDescription>
                {t('Where document files are stored right now.', 'أين تُخزَّن ملفات المستندات حالياً.')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <Badge
                  data-testid="storage-provider-badge"
                  className={healthy
                    ? 'bg-green-100 text-green-800 border-green-200'
                    : 'bg-red-100 text-red-800 border-red-200'}
                >
                  {healthy ? <CheckCircle className="h-3.5 w-3.5 me-1" /> : <XCircle className="h-3.5 w-3.5 me-1" />}
                  {String(provider || 'unknown').toUpperCase()} · {healthy ? t('Connected', 'متصل') : t('Not reachable', 'غير متصل')}
                </Badge>
                {provider === 'local' && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                    <AlertTriangle className="h-3.5 w-3.5 me-1" />
                    {t('Ephemeral — files lost on redeploy', 'مؤقّت — تُفقد الملفات عند إعادة النشر')}
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('Bucket', 'الحاوية')}</span>
                  <span className="font-medium">{data.provider_bucket || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('Endpoint', 'نقطة النهاية')}</span>
                  <span className="font-medium truncate max-w-[60%]" title={data.provider_endpoint || ''}>
                    {data.provider_endpoint || '—'}
                  </span>
                </div>
              </div>
              {conn && conn.ok === false && (
                <div className="mt-2 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="storage-connection-error">
                  <p className="font-medium">{t('Connection error', 'خطأ في الاتصال')}: {conn.error}</p>
                  {conn.hint && <p className="text-red-600/80 mt-1">{conn.hint}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard testid="stat-total-docs" title={t('Total documents', 'إجمالي المستندات')} value={data.total ?? 0} icon={HardDrive} colorClass="text-slate-600" />
            <StatCard testid="stat-ok-docs" title={t('OK (retrievable)', 'سليمة (قابلة للاسترجاع)')} value={data.ok ?? 0} icon={CheckCircle} colorClass="text-green-600" />
            <StatCard testid="stat-broken-docs" title={t('Broken (file missing)', 'مفقودة (ملف غير موجود)')} value={data.broken ?? 0} icon={FileWarning} colorClass="text-red-600" />
          </div>

          {/* Broken files checklist */}
          <Card data-testid="broken-files-card">
            <CardHeader>
              <CardTitle className="text-lg">{t('Broken files', 'الملفات المفقودة')}</CardTitle>
              <CardDescription>
                {t('Each file below is missing from storage. Click "Re-upload" to restore it in place — the document keeps its type, expiry and history.',
                   'كل ملف أدناه مفقود من التخزين. اضغط "إعادة رفع" لاستعادته دون تغيير نوع المستند أو تاريخ انتهائه أو سجله.')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {broken.length === 0 ? (
                <div className="flex items-center justify-center py-10 text-slate-400" data-testid="no-broken-files">
                  <CheckCircle className="h-8 w-8 me-2 text-green-500" />
                  <span>{t('No broken files — everything is retrievable.', 'لا توجد ملفات مفقودة — كل شيء قابل للاسترجاع.')}</span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('Document', 'المستند')}</TableHead>
                        <TableHead>{t('Owner', 'المالك')}</TableHead>
                        <TableHead>{t('Scope', 'النطاق')}</TableHead>
                        <TableHead>{t('Uploaded', 'تاريخ الرفع')}</TableHead>
                        <TableHead>{t('Reason', 'السبب')}</TableHead>
                        <TableHead className="text-end">{t('Action', 'إجراء')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {broken.map((d) => (
                        <TableRow key={d.id} data-testid={`broken-row-${d.id}`}>
                          <TableCell className="font-medium max-w-[220px] truncate" title={d.name || d.file_name}>
                            {d.name || d.file_name || '—'}
                            {d.file_name && d.name !== d.file_name && (
                              <span className="block text-xs text-slate-400 truncate">{d.file_name}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {d.owner || '—'}
                            {d.employee_number && <span className="block text-xs text-slate-400">#{d.employee_number}</span>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="capitalize">{d.scope || 'employee'}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">{fmtDate(d.uploaded_at)}</TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-[240px]" title={d.reason}>
                            <span className="line-clamp-2">{d.reason || '—'}</span>
                          </TableCell>
                          <TableCell className="text-end">
                            <Button
                              size="sm"
                              onClick={() => triggerPick(d.id)}
                              disabled={reuploadingId === d.id}
                              data-testid={`reupload-btn-${d.id}`}
                            >
                              {reuploadingId === d.id
                                ? <Loader2 className="h-4 w-4 me-2 animate-spin" />
                                : <Upload className="h-4 w-4 me-2" />}
                              {reuploadingId === d.id ? t('Uploading…', 'جارٍ الرفع…') : t('Re-upload', 'إعادة رفع')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {data.truncated && (
                    <p className="text-xs text-amber-600 mt-3">
                      {t('List truncated — resolve these, then Refresh to load more.',
                         'القائمة مقتطعة — عالج هذه ثم اضغط تحديث لتحميل المزيد.')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default StorageHealthPage;
